import { TRANSLATIONS } from './translations';
import { PillTone } from '../theme/creamyTokens';
import { AdminMoneyLocation } from '../services/AdminService';

/**
 * Labels and tones for the money vocabulary the admin panel shares between
 * Reservas, Finanzas and the money trail. One place, so a booking can't read
 * "en custodia" on one screen and something else on the next.
 */
export const MONEY_LOCATION_LABEL_KEYS: Record<AdminMoneyLocation, string> = {
  NotYetAsked: TRANSLATIONS.ADMIN_MONEY_LOC_NOT_YET_ASKED,
  AuthorizedOnCard: TRANSLATIONS.ADMIN_MONEY_LOC_AUTHORIZED,
  PlatformEscrow: TRANSLATIONS.ADMIN_MONEY_LOC_ESCROW,
  ReleasedToHost: TRANSLATIONS.ADMIN_MONEY_LOC_RELEASED,
  Refunded: TRANSLATIONS.ADMIN_MONEY_LOC_REFUNDED,
  NothingCharged: TRANSLATIONS.ADMIN_MONEY_LOC_NOTHING,
};

// Deliberately not "green = good". Escrow is the platform holding somebody
// else's money, which is a responsibility, not an achievement -- so it reads
// as the attention colour and "released" as the settled one.
export const MONEY_LOCATION_TONES: Record<AdminMoneyLocation, PillTone> = {
  NotYetAsked: 'default',
  AuthorizedOnCard: 'warning',
  PlatformEscrow: 'warning',
  ReleasedToHost: 'success',
  Refunded: 'default',
  NothingCharged: 'default',
};

export const MONEY_LOCATION_COLORS: Record<AdminMoneyLocation, string> = {
  NotYetAsked: 'default',
  AuthorizedOnCard: 'blue',
  PlatformEscrow: 'gold',
  ReleasedToHost: 'green',
  Refunded: 'default',
  NothingCharged: 'default',
};

export const CAPTURE_MODE_OPTIONS = ['Immediate', 'OnAcceptance', 'OnHandover'];

export const CAPTURE_MODE_LABEL_KEYS: Record<string, string> = {
  Immediate: TRANSLATIONS.ADMIN_CAPTURE_MODE_IMMEDIATE,
  OnAcceptance: TRANSLATIONS.ADMIN_CAPTURE_MODE_ON_ACCEPTANCE,
  OnHandover: TRANSLATIONS.ADMIN_CAPTURE_MODE_ON_HANDOVER,
};

export const DEPOSIT_COVERAGE_LABEL_KEYS: Record<string, string> = {
  Hold: TRANSLATIONS.ADMIN_DEPOSIT_COVERAGE_HOLD,
  SavedCard: TRANSLATIONS.ADMIN_DEPOSIT_COVERAGE_SAVED_CARD,
  None: TRANSLATIONS.ADMIN_DEPOSIT_COVERAGE_NONE,
};

export const DEPOSIT_COVERAGE_COLORS: Record<string, string> = {
  Hold: 'green',
  // Not an error and not a success: a saved card is a real answer, just a
  // weaker one than frozen funds (see DepositCoveragePolicy on the backend).
  SavedCard: 'blue',
  None: 'default',
};

export const DEPOSIT_STATE_OPTIONS = ['held', 'expiring', 'expired', 'failed', 'captured', 'released'];

export const DEPOSIT_STATE_LABEL_KEYS: Record<string, string> = {
  held: TRANSLATIONS.ADMIN_DEPOSITS_STATE_HELD,
  expiring: TRANSLATIONS.ADMIN_DEPOSITS_STATE_EXPIRING,
  expired: TRANSLATIONS.ADMIN_DEPOSITS_STATE_EXPIRED,
  failed: TRANSLATIONS.ADMIN_DEPOSITS_STATE_FAILED,
  captured: TRANSLATIONS.ADMIN_DEPOSITS_STATE_CAPTURED,
  released: TRANSLATIONS.ADMIN_DEPOSITS_STATE_RELEASED,
};

export const HOST_DEPOSIT_STATE_OPTIONS = ['low', 'open', 'closed'];

export const HOST_DEPOSIT_STATE_LABEL_KEYS: Record<string, string> = {
  low: TRANSLATIONS.ADMIN_HOST_DEPOSITS_STATE_LOW,
  open: TRANSLATIONS.ADMIN_HOST_DEPOSITS_STATE_OPEN,
  closed: TRANSLATIONS.ADMIN_HOST_DEPOSITS_STATE_CLOSED,
};

/**
 * One line per timeline event kind. Codes come from the server
 * (BookingMoneyTrailBuilder) and are mapped here rather than there, so the
 * wording is the panel's own and a new code never arrives as a sentence in
 * the wrong language.
 */
export const MONEY_EVENT_LABEL_KEYS: Record<string, string> = {
  BookingCreated: TRANSLATIONS.ADMIN_MONEY_EVENT_BOOKING_CREATED,
  PaymentAuthorizationAttempted: TRANSLATIONS.ADMIN_MONEY_EVENT_AUTH_ATTEMPTED,
  PaymentAuthorized: TRANSLATIONS.ADMIN_MONEY_EVENT_AUTHORIZED,
  DepositAuthorized: TRANSLATIONS.ADMIN_MONEY_EVENT_DEPOSIT_AUTHORIZED,
  DepositAuthorizationFailed: TRANSLATIONS.ADMIN_MONEY_EVENT_DEPOSIT_FAILED,
  DepositHoldExpires: TRANSLATIONS.ADMIN_MONEY_EVENT_DEPOSIT_EXPIRES,
  DepositReleaseScheduled: TRANSLATIONS.ADMIN_MONEY_EVENT_DEPOSIT_RELEASE_DUE,
  DepositReleased: TRANSLATIONS.ADMIN_MONEY_EVENT_DEPOSIT_RELEASED,
  DepositCaptured: TRANSLATIONS.ADMIN_MONEY_EVENT_DEPOSIT_CAPTURED,
  TransportStarted: TRANSLATIONS.ADMIN_MONEY_EVENT_TRANSPORT,
  HandedOver: TRANSLATIONS.ADMIN_MONEY_EVENT_HANDOVER,
  OffRent: TRANSLATIONS.ADMIN_MONEY_EVENT_OFF_RENT,
  CollectionStarted: TRANSLATIONS.ADMIN_MONEY_EVENT_COLLECTION,
  Returned: TRANSLATIONS.ADMIN_MONEY_EVENT_RETURNED,
  FundsReleasedToHost: TRANSLATIONS.ADMIN_MONEY_EVENT_FUNDS_RELEASED,
  LineTransferred: TRANSLATIONS.ADMIN_MONEY_EVENT_LINE_TRANSFERRED,
  LineTransferFailed: TRANSLATIONS.ADMIN_MONEY_EVENT_LINE_FAILED,
  Refunded: TRANSLATIONS.ADMIN_MONEY_EVENT_REFUNDED,
  RefundOutOfBand: TRANSLATIONS.ADMIN_MONEY_EVENT_REFUND_OUT_OF_BAND,
  InvoiceIssued: TRANSLATIONS.ADMIN_MONEY_EVENT_INVOICE,
  HostDepositMovement: TRANSLATIONS.ADMIN_MONEY_EVENT_HOST_DEPOSIT,
  DisputeSettled: TRANSLATIONS.ADMIN_MONEY_EVENT_DISPUTE_SETTLED,
  DisputeSettlementFailed: TRANSLATIONS.ADMIN_MONEY_EVENT_DISPUTE_FAILED,
};

export const REFUND_SOURCE_LABEL_KEYS: Record<string, string> = {
  Partial: TRANSLATIONS.ADMIN_MONEY_REFUND_SOURCE_PARTIAL,
  Cancellation: TRANSLATIONS.ADMIN_MONEY_REFUND_SOURCE_CANCELLATION,
  OutOfBand: TRANSLATIONS.ADMIN_MONEY_REFUND_SOURCE_OUT_OF_BAND,
};
